export interface Company {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  googleDriveRootFolderId?: string;
  settingsJson?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Country {
  id: number;
  code: string;
  name: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  company?: Company;
  companyId?: number;
  isActive: boolean;
  settingsJson?: string;
  createdAt?: string;
  updatedAt?: string;
}

export enum UserRole {
  ADMIN = "ADMIN", // Sitenin genel admini
  MANAGER = "MANAGER", // Yönetici
  PROJECT_MANAGER = "PROJECT_MANAGER", // Proje yöneticisi
  INTERN = "INTERN", // Stajyer
  EMPLOYEE = "EMPLOYEE", // Çalışan
  VISITOR = "VISITOR", // Ziyaretçi
}

export interface Workflow {
  id: number;
  name: string;
  isActive: boolean;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  workflowContainerId?: number;
}

// Backend-compatible Workflow interface
export interface BackendWorkflow {
  id?: number;
  name: string;
  isActive?: boolean;
  createdBy?: number;
  createdAt?: string;
  updatedAt?: string;
  nodes: BackendWorkflowNode[];
  edges: WorkflowEdge[];
  workflowContainerId?: number;
}

export interface WorkflowNode {
  id: string;
  type: NodeType;
  position: Position;
  data: NodeData;
  workflowId?: number;
}

// Backend-compatible WorkflowNode interface
export interface BackendWorkflowNode {
  id: string;
  type: NodeType;
  positionX: number;
  positionY: number;
  label: string;
  description?: string;
  assigneeId?: number;
  assigneeName?: string;
  activityId?: number;
  formData?: any;
  conditions?: any;
  notificationMessage?: string;
  approvalRequired?: boolean;
  dueDate?: string;
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
  condition?: EdgeCondition;
  workflowId?: number;
}

export enum NodeType {
  START = "START",
  ACTIVITY = "ACTIVITY", // Eski TASK -> yeni AKTİVİTE
  END = "END",
  // New activity pool types
  POOL_ACTIVITY = "POOL_ACTIVITY", // Activity from pool
}

export interface Position {
  x: number;
  y: number;
}

export interface NodeData {
  label: string;
  description?: string;
  assigneeId?: number;
  assigneeName?: string;
  formData?: Record<string, any>;
  conditions?: DecisionCondition[];
  notificationMessage?: string;
  approvalRequired?: boolean;
  dueDate?: string;
  budgetThreshold?: number;
  archiveConfirmation?: boolean;
}

export interface CustomNodeData extends NodeData {
  id?: string; // Node ID for TaskFormPanel compatibility
  onEdit?: (nodeId: string) => void;
  onView?: (nodeId: string) => void;
  onSaveAsTemplate?: (nodeId: string) => void;
  onDelete?: (nodeId: string) => void;
  taskFormData?: TaskFormData; // Task form data for task nodes (legacy)
  // New modular activity data
  activityDefinitionId?: number; // Reference to ActivityDefinition for pool activities
  activityDefinition?: ActivityDefinition; // Full activity definition for pool activities
  selectedForms?: number[]; // Form IDs selected for this activity instance
  // Pending changes support
  isPendingActivity?: boolean; // True if activity hasn't been saved to database yet
  pendingForms?: Form[]; // Forms that need to be saved when workflow is saved
}

export interface DecisionCondition {
  id: string;
  field: string;
  operator: ConditionOperator;
  value: any;
  targetNodeId: string;
  label: string;
}

export enum ConditionOperator {
  EQUALS = "equals",
  NOT_EQUALS = "not_equals",
  GREATER_THAN = "greater_than",
  LESS_THAN = "less_than",
  GREATER_EQUAL = "greater_equal",
  LESS_EQUAL = "less_equal",
  CONTAINS = "contains",
}

export interface EdgeCondition {
  field: string;
  operator: ConditionOperator;
  value: any;
}

export interface Activity {
  id: number;
  workflowId: number;
  nodeId: string;
  userId: number;
  userName: string;
  action: string;
  description: string;
  formData?: Record<string, any>;
  createdAt: string;
}

export interface WorkflowExecution {
  id: number;
  workflowId: number;
  currentNodeId: string;
  status: ExecutionStatus;
  executionData: Record<string, any>;
  startedAt: string;
  completedAt?: string;
}

export enum ExecutionStatus {
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  PAUSED = "PAUSED",
}

export interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  size: number;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

// Form Builder Types (New Modular Approach)
export interface Form {
  id: number;
  title: string;
  description?: string;
  formStructure: FormStructure;
  submissionData?: Record<string, any>; // User's submitted form data
  createdBy: number;
  createdByName?: string;
  isPublic: boolean;
  isArchived: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
}

export interface FormStructure {
  elements: FormElement[];
  elementOrder: string[]; // Array of element IDs
  settings?: {
    theme?: string;
    validation?: FormValidationSettings;
  };
}

export interface FormElement {
  id: string;
  type:
    | "text"
    | "textarea"
    | "number"
    | "date"
    | "select"
    | "checkbox"
    | "heading"
    | "budget_input"
    | "date_range"
    | "team_member_select"
    | "project_type_select"
    | "milestone_tracker"
    | "progress_bar"
    | "cost_calculator"
    | "client_select"
    | "progress_method_select"
    | "project_duration"
    | "warning_threshold"
    | "notification_roles"
    | "country_select"
    | "city_select"
    | "employee_select"
    | "offer_amount";
  label: string;
  value?: string;
  required?: boolean;
  options?: string[]; // For select elements
  allowMultiple?: boolean; // For non-location select elements
  placeholder?: string;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
}

export interface FormValidationSettings {
  showErrors?: boolean;
  validateOnBlur?: boolean;
  validateOnSubmit?: boolean;
}

// Activity Types (New Modular Approach)
export interface ActivityDefinition {
  id: number;
  title: string;
  description?: string;
  createdBy: number;
  createdByName?: string;
  isPublic: boolean;
  isArchived: boolean;
  isActive: boolean;
  version: number;
  isLeave?: boolean; // If true, exclude from worked-hour totals
  tags?: string;
  icon?: string;
  color?: string;
  estimatedDurationMinutes?: number;
  requiresApproval: boolean;
  activityForms: ActivityFormAssignment[];
  startDate?: string;
  endDate?: string;
  assignedUserIds?: number[]; // Frontend format for UI
  assignedUsers?: string; // Backend format for API calls (JSON string)
  attachments?: string; // JSON string of GoogleDriveAttachment[]
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
}

export interface ActivityFormAssignment {
  id: number;
  form: Form;
  displayOrder: number;
  isRequired: boolean;
  isVisible: boolean;
  conditionExpression?: string;
}

export interface WorkflowTemplate {
  id: number;
  title: string;
  description?: string;
  category?: string;
  tags?: string;
  createdBy: number;
  createdByName?: string;
  isPublic: boolean;
  workflowContainerFormElements?: string; // JSON string of workflow container form element definitions
  createdAt: string;
  updatedAt: string;
  nodeTemplates?: WorkflowNodeTemplate[];
  edgeTemplates?: WorkflowEdgeTemplate[];
}

export interface WorkflowNodeTemplate {
  id?: number;
  nodeId: string; // Backend'den gelen field name
  nodeType: string;
  nodeData: string;
  positionX?: number; // Backend'den gelen separate fields
  positionY?: number;
  workflowTemplate?: WorkflowTemplate;
}

export interface WorkflowEdgeTemplate {
  id?: number;
  edgeId: string; // Backend'den gelen field name
  sourceNodeId: string;
  targetNodeId: string;
  sourceHandle?: string;
  targetHandle?: string;
  edgeData?: string;
  workflowTemplate?: WorkflowTemplate;
}

// Legacy Task Form Types (for backward compatibility)
export interface TaskFormElement extends FormElement {}

export interface TaskFormData {
  id?: number;
  taskId?: number;
  workflowId?: number;
  formName: string;
  formDescription?: string;
  elements: TaskFormElement[];
  elementOrder: string; // JSON string of element IDs
  submissionData?: Record<string, any>; // Submitted form data
  isTemplate?: boolean; // Whether this is a reusable template
  createdAt?: string;
  updatedAt?: string;
  // New multi-form support
  multipleFormsData?: Array<{
    id: number;
    title: string;
    description: string;
    formStructure: {
      elements: TaskFormElement[];
      elementOrder: string[];
    };
    submissionData?: Record<string, any>; // User's submitted form data
    displayOrder: number;
    isFromLibrary?: boolean;
  }>;
  // Activity basic information
  activityName?: string;
  activityDescription?: string;
  activityId?: number; // Reference to saved activity in database
  isLeave?: boolean; // If true, exclude from worked-hour totals
  startDate?: string;
  endDate?: string;
  assignedUserIds?: number[];
  // Google Drive attachments
  attachments?: GoogleDriveAttachment[];
}

// Project Management Types
export interface WorkflowContainer {
  id: number;
  status: WorkflowContainerStatus;
  managerId?: number;
  managerName?: string;
  formElements?: ProjectFormElement[];
  workflowContainerFormData?: {
    name?: string;
    projectName?: string;
    clientName?: string;
    client?: string;
    projectType?: string;
    startDate?: string;
    endDate?: string;
    budget?: number;
    [key: string]: any; // Allow additional dynamic properties
  };
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
}

export enum WorkflowContainerStatus {
  PLANNING = "PLANNING",
  IN_PROGRESS = "IN_PROGRESS",
  ON_HOLD = "ON_HOLD",
  UNDER_REVIEW = "UNDER_REVIEW",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
  ARCHIVED = "ARCHIVED",
}

export interface WorkflowContainerMember {
  id: number;
  workflowContainerId: number;
  userId: number;
  role: string; // "team_member", "project_manager", etc.
  canStartWorkflow: boolean;
  addedAt: string;
  user?: {
    id: number;
    firstName: string;
    lastName: string;
    username: string;
    email: string;
    role?: UserRole; // Optional: available when backend includes it or when enriched client-side
  };
}

// Extended form elements for project management
export interface ProjectFormElement {
  id: string;
  _key?: string; // Internal stable key for React rendering (prevents focus loss on updates)
  type:
    | "text"
    | "textarea"
    | "number"
    | "date"
    | "select"
    | "checkbox"
    | "heading"
    | "budget_input"
    | "date_range"
    | "team_member_select"
    | "project_type_select"
    | "milestone_tracker"
    | "progress_bar"
    | "cost_calculator"
    | "client_select"
    | "progress_method_select"
    | "project_duration"
    | "warning_threshold"
    | "notification_roles"
    | "country_select"
    | "city_select"
    | "employee_select"
    | "currency_input"
    | "offer_amount"
    | "province_select"
    | "district_select"
    | "neighborhood_select"
    | "invoice";
  label: string;
  value?: string;
  required?: boolean;
  options?: string[]; // For select elements
  allowMultiple?: boolean; // For non-location select elements
  placeholder?: string;
  currency?: string; // For currency_input elements (TRY, USD, EUR, etc.)
  saveToList?: boolean; // Store user responses as suggestions for this field
  showInList?: boolean; // Show this element as a column in project lists
  listColumnOrder?: number; // Order (1-10) for list columns
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
}

export interface WorkflowFormValueSuggestion {
  id: number;
  elementId: string;
  value: string;
  usageCount?: number;
  lastUsedAt?: string;
}

// Activity Report Types (for manager dashboard and reporting)
export interface ActivityReport {
  activityId: number;
  templateActivityId?: number;
  isLeave?: boolean;
  requiresApproval?: boolean;
  activityInstanceName?: string;
  activityTitle: string;
  activityDescription?: string;
  activityStatus: string; // "PENDING" | "ACTIVE" | "COMPLETED" | "REJECTED" | "PENDING_APPROVAL"
  activityCreatedAt: string;
  activityCompletedAt?: string;
  activityDueDate?: string;
  submitDescription?: string; // User's submission note when completing or adding activity
  assigneeId?: number;
  assigneeName?: string;
  workflowContainerId?: number;
  projectName?: string;
  workflowId?: number;
  workflowName?: string;
  formSubmissions?: ActivityFormSubmission[];
  attachments?: string; // JSON array of GoogleDriveAttachment objects
  duration?: number; // Duration in hours to complete the activity (total)
  userDurations?: string; // JSON object: {"userId": duration}
  userFormData?: string; // JSON object: {"userId": {formData}}
  completedUserIds?: string; // JSON array of user IDs who completed the task
  assignedUsers?: string; // JSON array or comma-separated string of assigned user IDs
  workflowNodeId?: string;
  formContentSummary?: string;
  formContentTooltip?: string;
  isInvoiced?: boolean;
}

export interface ActivityFormSubmission {
  formId: number;
  formName: string;
  formDescription?: string;
  formStructure?: string; // JSON string - field definitions with labels
  submissionData?: string; // JSON string - user submitted data
  submittedAt?: string;
  userName?: string; // Name of user who submitted
  userEmail?: string; // Email of user who submitted
}

export interface ActivityReportFilters {
  tab?: "activities" | "leaves";
  searchTerm?: string;
  workflowContainerId?: number;
  workflowInstanceId?: number;
  workflowContainerName?: string;
  workflowName?: string;
  activityTitle?: string;
  status?: string; // "PENDING" | "ACTIVE" | "COMPLETED" | "REJECTED" | "PENDING_APPROVAL"
  assigneeId?: number;
  startDate?: string;
  endDate?: string;
}

export interface ActivityReportListResponse {
  items: ActivityReport[];
  page: number;
  size: number;
  totalCount: number;
  hasNext: boolean;
}

// Google Drive Attachment
export interface GoogleDriveAttachment {
  id: string;
  name: string;
  url: string;
  mimeType: string;
  iconUrl?: string;
  thumbnailUrl?: string;
  description?: string;
  category?: string;
  uploadedAt?: string;
  uploadedBy?: number;
  uploadedByName?: string;
  addedAt: string;
  addedBy?: number;
}

export interface ProjectCollection {
  id: number;
  companyId: number;
  workflowInstanceId: number;
  collectionDate: string;
  amount: number;
  currency: string;
  description?: string | null;
  attachments: GoogleDriveAttachment[];
  createdByUserId?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}
